import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDebounceValue } from 'usehooks-ts';
import { IoMdAdd, IoMdDownload } from 'react-icons/io';
import { toast } from 'react-toastify';
import { useGetClients, useGetClientsList } from '~/hooks/clients';
import { useUserProfile } from '~/hooks/auth';
import { exportClients } from '~/api/clients';
import InputSearch from '~/components/InputSearch';
import FilterSearchParam from '~/components/FilterSearchParam';
import styles from './clients.module.scss';
import ClientsTable from '~/components/table/ClientsTable';
import Modal from '~/lib/Modal';
import CreateClient from '~/components/client/CreateClient';
import Pagination from '~/components/Pagination';

const ListClients = () => {
  const modalRef = useRef<any>(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { search = '', ...paramsObject } = Object.fromEntries(searchParams);
  const [debouncedSearch] = useDebounceValue(search, 500);
  const { data: list } = useGetClientsList();
  const { data: user } = useUserProfile();
  const isAdmin = user?.role === 'Admin';
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    const hasPagination = params.has('limit') || params.has('page') || params.has('skip');
    if (!hasPagination) {
      params.set('page', '1');
      params.set('limit', '20');
      setSearchParams(params, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const { data: dataClients } = useGetClients({
    ...paramsObject,
    search: debouncedSearch,
    limit: paramsObject.limit || '20',
    page: paramsObject.page || '1',
  });
  const { clients, meta } = dataClients || {};
  const { agencies = [], departments = [] } = list || {};

  const handleSearch = (searchValue: string) => {
    const params = new URLSearchParams(searchParams);
    params.delete('skip');
    params.delete('page');
    if (searchValue.length < 3) {
      params.delete('search');
    } else {
      params.set('search', searchValue);
    }
    setSearchParams(params);
  };

  const handleSorting = (sort: any) => {
    if (!sort) return;
    const params = new URLSearchParams(searchParams);
    params.delete('skip');
    params.delete('page');
    params.set('sortKey', sort.id.replace('_', '.'));
    params.set('order', sort.desc ? 'desc' : 'asc');
    setSearchParams(new URLSearchParams(params));
  };

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      await exportClients();
    } catch (error) {
      toast.error('Erreur lors de l\'export des clients');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <div className={styles.content}>
        <div className={styles.listClients}>
          <div className={styles.header}>
            <h1>Les clients</h1>
            <div className={styles.filters}>
              <div className={styles.selects}>
                <div className={styles.field}>
                  <FilterSearchParam
                    label='Agence'
                    name='agency'
                    options={agencies}
                    feminine
                  />
                </div>
                <div className={styles.field}>
                  <FilterSearchParam
                    label='Département'
                    name='department'
                    options={departments}
                  />
                </div>
              </div>
              <div className={styles.right}>
                <div className={styles.search}>
                  <InputSearch
                    defaultValue={search}
                    handleSearch={handleSearch}
                    label='Rechercher'
                  />
                </div>
                <button onClick={() => modalRef.current.open()}>
                  <span className='icon'>{IoMdAdd({})}</span>
                  <span>Créer un client</span>
                </button>
                {isAdmin && (
                  <button onClick={handleExport} disabled={isExporting}>
                    <span className='icon'>{IoMdDownload({})}</span>
                    <span>{isExporting ? 'Export...' : 'Exporter'}</span>
                  </button>
                )}
                </div>
            </div>
          </div>
          <ClientsTable
            clients={clients || []}
            handleSorting={handleSorting}
          />
          <div className={styles.pagination}>
            {meta?.total && meta?.limit && meta?.page
            && meta.total > meta.limit
            && <Pagination
              currentPage={meta.page}
              totalItems={meta.total}
              itemsPerPage={meta.limit}
            />
            }
          </div>
        </div>
      </div>
      <Modal width={450} ref={modalRef}>
        <CreateClient
          onSuccess={(clientId: string) => {
            modalRef.current.close();
            navigate(`/clients/${clientId}`);
          }}
          close={() => modalRef.current.close()}
        />
      </Modal>
    </>
  );
};

export default ListClients;
